/**
 * The query-key factory (`111-frontend-architecture.md` §3.1).
 *
 * NEVER inline a key array anywhere else. A typo'd inline key is a cache miss
 * that looks like a bug in the server.
 *
 * The hierarchy is load-bearing: `invalidateQueries({ queryKey: keys.pages.all() })`
 * invalidates the tree, every detail and every backlink list in one call, because
 * they share the `['pages']` prefix.
 *
 * Later milestones fill in the rest of the table from §3.1 — the keys below are the
 * ones M1 actually uses. Add, do not restructure.
 */
export const keys = {
  session: () => ['session'] as const,
  settings: () => ['settings'] as const,
  /** `GET /api/storage` — the settings screen's "Storage used" figures. Informational. */
  storage: () => ['storage'] as const,

  pages: {
    all: () => ['pages'] as const,
    tree: () => ['pages', 'tree'] as const,
    trash: () => ['pages', 'tree', 'archived'] as const,
    detail: (id: string) => ['pages', 'detail', id] as const,
    backlinks: (id: string) => ['pages', 'backlinks', id] as const,
    /**
     * The whole backlink prefix. A content save re-derives `page_link` server-side
     * (`070` §7.3), so OTHER pages' backlink lists may have gained or lost this page —
     * and we cannot know which (`111` §3.3). Invalidate the lot.
     */
    allBacklinks: () => ['pages', 'backlinks'] as const,
    /** The events linked to this page (`080` §8, "Page → events"). */
    events: (id: string) => ['pages', 'events', id] as const,
  },

  /**
   * `111` §3.1. The three views SHARE this cache: the agenda, the week and the month all
   * key on `(from, to)`, so two views showing the same range hit one entry — and a range
   * change is a new key, which is what makes a refetch happen at all.
   *
   * `range` is nested under `all` so that an event mutation can invalidate every cached
   * range with one prefix call when it has to (`111` §3.3).
   */
  calendar: {
    all: () => ['calendar'] as const,
    range: (from: string, to: string) => ['calendar', 'range', from, to] as const,
  },

  eventCategories: () => ['event-categories'] as const,

  tags: {
    all: () => ['tags'] as const,
    pages: (id: string) => ['tags', id, 'pages'] as const,
  },

  /**
   * One entry per query string. `gcTime` is short (`111` §3.2) precisely because this
   * key is unbounded — a keystroke is a cache entry.
   */
  search: (q: string) => ['search', q] as const,

  /**
   * The AI surface (`111` §3.1). `detail` has `staleTime: 0` — a stream just mutated it
   * server-side, and the client's copy of the assistant message during the stream is a
   * local string, not cache truth. `budget` is `staleTime: 0` too: it must reflect the
   * CURRENT content of the attached pages, and a page edit changes the token count
   * without touching the conversation (`090` §2).
   */
  conversations: {
    all: () => ['conversations'] as const,
    list: () => ['conversations', 'list'] as const,
    detail: (id: string) => ['conversations', 'detail', id] as const,
    budget: (id: string) => ['conversations', 'budget', id] as const,
  },

  systemPrompts: () => ['system-prompts'] as const,
} as const;
